import React, { useCallback, useRef, useState } from 'react';
import { Col, Container, Row, Button, Table } from 'react-bootstrap';
import Webcam from 'react-webcam';
import imagePlaceholder from '../assets/images/imagePlaceholder.svg';
import { FaCamera, FaFolder } from 'react-icons/fa';
import { BiAnalyse } from 'react-icons/bi';
import SweetAlert from 'react-bootstrap-sweetalert';
import * as faceapi from 'face-api.js';
import Loader from './Loader';

const Camera = () => {

    const [capturedPhoto, setCapturedPhoto] = useState(imagePlaceholder);
    const [cameraError, setCameraError] = useState(false);
    const webcamRef = useRef(null);

    const [loader, setLoader] = useState('d-none');
    const [age, setAge] = useState(null);
    const [gender, setGender] = useState(null);
    const [expressions, setExpressions] = useState(null);

    const [leftToRightEye, setLeftToRightEye] = useState(null);
    const [noseToLeftEye, setNoseToLeftEye] = useState(null);
    const [noseToRightEye, setNoseToRightEye] = useState(null);
    const [noseToLeftMouth, setNoseToLeftMouth] = useState(null);
    const [noseToRightMouth, setNoseToRightMouth] = useState(null);

    const capture = useCallback(
        () => {
            const photoBase64 = webcamRef.current.getScreenshot();
            setCapturedPhoto(photoBase64);
        },
        [webcamRef]
    );

    const saveImage = () => {
        const anchorTag = document.createElement('a');
        anchorTag.href = capturedPhoto;
        anchorTag.download = 'webcamImage.jpeg';
        anchorTag.click();
    }

    const onCameraTryAgain = () => {
        window.location.href = "/";
    }

    const onCancel = () => {
        setCameraError(true);
    }

    const faceAnalysis = async () => {
            setLoader('d-block');

            await faceapi.nets.ssdMobilenetv1.loadFromUri('models/');
            await faceapi.nets.faceLandmark68Net.loadFromUri('models/');
            await faceapi.nets.faceExpressionNet.loadFromUri('models/');
            await faceapi.nets.ageGenderNet.loadFromUri('models/');

            const imageID = document.getElementById('img');
            const detection = await faceapi.detectAllFaces(imageID)
                .withFaceLandmarks()
                .withFaceExpressions()
                .withAgeAndGender();
            
            // Age and Gender Detection
            setAge((detection[0]['age']).toFixed(0));
            setGender(detection[0]['gender']);

            // Expression
            const neutral = (detection[0]['expressions']['neutral']);
            const happy = (detection[0]['expressions']['happy']);
            const sad = (detection[0]['expressions']['sad']);
            const angry = (detection[0]['expressions']['angry']);
            const fearful = (detection[0]['expressions']['fearful']);
            const disgusted = (detection[0]['expressions']['disgusted']);
            const surprised = (detection[0]['expressions']['surprised']);

            if (neutral >= 0.9 && neutral <= 1.1) {
                setExpressions('Neutral');
            }
            else if (happy >= 0.9 && happy <= 1.1) {
                setExpressions('Happy');
            }
            else if (sad >= 0.9 && sad <= 1.1) {
                setExpressions('Sad');
            }
            else if (angry >= 0.9 && angry <= 1.1) {
                setExpressions('Angry');
            }
            else if (fearful >= 0.9 && fearful <= 1.1) {
                setExpressions('Fearful');
            }
            else if (disgusted >= 0.9 && disgusted <= 1.1) {
                setExpressions('Disgusted');
            }
            else if (surprised >= 0.9 && surprised <= 1.1) {
                setExpressions('Surprised');
            }
            else {
                setExpressions('Unable to define');
            }

            // Landmark Distances...
            const landmarkPoint = detection[0]['landmarks']['_positions'];

            setLeftToRightEye(calculateDistance(landmarkPoint[37], landmarkPoint[46]));
            setNoseToLeftEye(calculateDistance(landmarkPoint[31], landmarkPoint[37]));
            setNoseToRightEye(calculateDistance(landmarkPoint[31], landmarkPoint[46]));
            setNoseToLeftMouth(calculateDistance(landmarkPoint[31], landmarkPoint[49]));
            setNoseToRightMouth(calculateDistance(landmarkPoint[31], landmarkPoint[55]));

            setLoader('d-none');
    }

    function calculateDistance (firstPoint, secondPoint) {
        const x1 = firstPoint['_x'];
        const y1 = firstPoint['_y'];
        const x2 = secondPoint['_x'];
        const y2 = secondPoint['_y'];
        const distance = Math.sqrt(Math.pow((x1 - x2), 2) + Math.pow((y1 - y2), 2));
        return distance.toFixed(2);
    }

    return (
        <>
            <Container>
                <Row className="d-flex justify-content-center">
                    <Col className="p-2 text-center" lg={4} md={4} sm={12}>
                        <Webcam
                            ref={webcamRef}
                            className='w-100 rounded'
                            audio={false}
                            screenshotFormat="image/jpeg"
                            onUserMediaError={() => setCameraError(true)}
                        />
                        <Button onClick={capture} className='mt-2 btn-danger'><FaCamera /> Capture</Button>
                    </Col>
                    <Col className="p-2 text-center" lg={4} md={4} sm={12}>
                        <img id='img' src={capturedPhoto} className='w-100 rounded' alt="not shown" />
                        <Button onClick={saveImage} className='mx-1 mt-3 btn-danger'><FaFolder /> Save</Button>
                        <Button onClick={faceAnalysis} className='mx-1 mt-3 btn-danger'><BiAnalyse /> Analysis</Button>
                    </Col>
                </Row>
            </Container>

            <Container fluid>
                <Row className='m-2 py-5'>
                    <Col>
                        <Table>
                            <thead>
                                <tr>
                                    <th className="text-danger">Age</th>
                                    <th className="text-white">{age}</th>
                                    <th className="text-danger">Gender</th>
                                    <th className="text-white">{gender}</th>
                                    <th className="text-danger">Expression</th>
                                    <th className="text-white">{expressions}</th>
                                    <th className="text-danger">Left Eye To Right Eye</th>
                                    <th className="text-white">{leftToRightEye}</th>
                                </tr>
                                <tr>
                                    
                                </tr>
                                <tr>
                                    <th className="text-danger">Nose To Left Mouth</th>
                                    <th className="text-white">{noseToLeftMouth}</th>
                                    <th className="text-danger">Nose To Right Mouth</th>
                                    <th className="text-white">{noseToRightMouth}</th>
                                    <th className="text-danger">Nose To Left Eye</th>
                                    <th className="text-white">{noseToLeftEye}</th>
                                    <th className="text-danger">Nose To Right Eye</th>
                                    <th className="text-white">{noseToRightEye}</th>
                                </tr>
                            </thead>
                        </Table>
                    </Col>
                </Row>
            </Container>

            <div className={loader}>
                <Loader />
            </div>
            {cameraError && <SweetAlert danger title="Device Camera Not Working" onConfirm={onCameraTryAgain} onCancel={onCancel} />}
        </>
    );
};

export default Camera;